import React from 'react';
import { MapPin, Calendar, CreditCard as Edit2, Image as ImageIcon, DollarSign, Trash2, MessageCircle } from 'lucide-react';
import { Post } from '../types';
import { Badge } from './Badge';
import { Button } from './Button';

interface PostCardProps {
  post: Post;
  onEdit?: (post: Post) => void;
  onDelete?: (post: Post) => void;
  onContact?: (post: Post) => void;
  viewMode?: 'owner' | 'provider';
}

const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Inactive':
      return 'warning';
    case 'Completed':
      return 'info';
    case 'Cancelled':
      return 'error';
    default:
      return 'info';
  }
};

const PostCard: React.FC<PostCardProps> = ({ post, onEdit, onDelete, onContact, viewMode = 'owner' }) => {
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const day = date.getDate().toString().padStart(2, '0');
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${minutes}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200 flex flex-col min-h-[600px]">
      {post.photo ? (
        <div className="h-48 w-full overflow-hidden bg-gray-100 flex-shrink-0">
          <img
            src={post.photo}
            alt={post.title}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="h-48 w-full bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center flex-shrink-0">
          <ImageIcon className="w-16 h-16 text-blue-300" />
        </div>
      )}

      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 flex-1">
            {post.title}
          </h3>
          <Badge variant={getStatusColor(post.status)} className="ml-2 flex-shrink-0">
            {post.status}
          </Badge>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {post.description}
        </p>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Badge variant="info" className="text-xs">
              {post.careType}
            </Badge>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <MapPin className="w-4 h-4 text-gray-400" />
            <span>
              {post.city}, {post.postalCode}
            </span>
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span>{formatDate(post.appointmentDate)}</span>
          </div>

          <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <DollarSign className="w-4 h-4 text-green-600" />
            <span className="text-green-600">£{post.budget}</span>
          </div>
        </div>

        <div className="mt-auto">
          {viewMode === 'owner' && (
            <div className="flex gap-2">
              {onEdit && (
                <Button
                  onClick={() => onEdit(post)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Button>
              )}
              {onDelete && (
                <Button
                  onClick={() => onDelete(post)}
                  variant="outline"
                  className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:bg-red-50 border-red-200"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </Button>
              )}
            </div>
          )}

          {viewMode === 'provider' && onContact && (
            <Button
              onClick={() => onContact(post)}
              className="w-full flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Service User
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostCard;
